import { JUDGEMENT_STATUS } from '@/constants/ui/map/legendConstants'
import {
  type AiCollapseJudgment,
  type EmergencyInspection,
  type JudgementStatusType,
  type ResidentialDamageInspection
} from '@/types/api/mapViewData/mapViewDataType'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useGovernmentSettingStore } from './governmentSettingStore'

export const useMapPointCloudSettingsStore = defineStore('mapPointCloudSettingsStore', () => {
  const { includesFeatureOptionsEmergency } = useGovernmentSettingStore()

  /** デフォルト値（住家被害認定調査のレイヤ設定項目） */
  const defaultResidentialDamageInspection: ResidentialDamageInspection = {
    partiallyDamaged: true,
    semiPartiallyDestroyed: true,
    partiallyDestroyed: true,
    halfTotallyDestroyed: true,
    almostTotallyDestroyed: true,
    totallyDestroyed: true,
    floodingUnderFloor: true,
    floodingAboveFloor: true
  }
  /** デフォルト値（AI倒壊判定のレイヤ設定項目） */
  const defaultAiCollapseJudgment: AiCollapseJudgment = {
    notTotallyDestroyed: true,
    totalCollapse: true
  }
  /** デフォルト値（応急危険度判定調査のレイヤ設定項目） */
  const defaultEmergencyInspection = (): EmergencyInspection => ({
    inspected: includesFeatureOptionsEmergency(),
    warning: includesFeatureOptionsEmergency(),
    danger: includesFeatureOptionsEmergency()
  })

  // state
  /** 有効化されている判定状況 */
  const activeJudgementStatus = ref<JudgementStatusType>(JUDGEMENT_STATUS.JUDGED)
  /** 住家被害認定調査のレイヤ設定項目 */
  const residentialDamageInspection = ref<ResidentialDamageInspection>({
    ...defaultResidentialDamageInspection
  })
  /** AI倒壊判定のレイヤ設定項目 */
  const aiCollapseJudgment = ref<AiCollapseJudgment>({
    ...defaultAiCollapseJudgment
  })
  /** 応急危険度判定調査のレイヤ設定項目 */
  const emergencyInspection = ref<EmergencyInspection>({
    ...defaultEmergencyInspection()
  })

  const residentialDamageInspectionKeys = Object.keys(
    residentialDamageInspection.value
  ) as (keyof ResidentialDamageInspection)[]
  const aiCollapseJudgmentKeys = Object.keys(
    aiCollapseJudgment.value
  ) as (keyof AiCollapseJudgment)[]
  const emergencyInspectionKeys = Object.keys(
    emergencyInspection.value
  ) as (keyof EmergencyInspection)[]

  /**
   * storeの状態をリセットする
   */
  const reset = () => {
    activeJudgementStatus.value = JUDGEMENT_STATUS.JUDGED
    residentialDamageInspection.value = { ...defaultResidentialDamageInspection }
    aiCollapseJudgment.value = { ...defaultAiCollapseJudgment }
    emergencyInspection.value = { ...defaultEmergencyInspection() }
  }

  /**
   * 判定済み配下すべてのチェックをオン・オフする関数
   * @param checked セットする値
   * @returns なし
   */
  const setAllJudgedCheckboxes = (checked: boolean): void => {
    if (checked) {
      residentialDamageInspection.value = { ...defaultResidentialDamageInspection }
      aiCollapseJudgment.value = { ...defaultAiCollapseJudgment }
      emergencyInspection.value = { ...defaultEmergencyInspection() }
    } else {
      residentialDamageInspectionKeys.forEach(key => {
        residentialDamageInspection.value[key] = false
      })
      aiCollapseJudgmentKeys.forEach(key => {
        aiCollapseJudgment.value[key] = false
      })
      emergencyInspectionKeys.forEach(key => {
        emergencyInspection.value[key] = false
      })
    }
  }

  /**
   * 判定済みチェックボックス群のいずれかが選択されているか判定
   * @param なし
   * @returns 選択有無（true: 有, false: 無）
   */
  const isAnyJudgedCheckboxChecked = (): boolean => {
    return (
      residentialDamageInspectionKeys.some(key => residentialDamageInspection.value[key]) ||
      aiCollapseJudgmentKeys.some(key => aiCollapseJudgment.value[key]) ||
      emergencyInspectionKeys.some(key => emergencyInspection.value[key])
    )
  }

  return {
    activeJudgementStatus,
    residentialDamageInspection,
    aiCollapseJudgment,
    emergencyInspection,
    reset,
    setAllJudgedCheckboxes,
    isAnyJudgedCheckboxChecked
  }
})
